import React, {useEffect, useMemo, useRef, useState} from 'react'

import {Context} from './MarginContext'
import {Margin, MarginRectStrategy, MarginStrategyEffect} from './types'
import {getNextMargin, isMarginEqual} from './utils'

type Props = {
  offset?: Margin
  debug?: boolean
}

const resizeEventEffect = (func: VoidFunction) => {
  let timeoutID: number | undefined

  function throttler() {
    window.clearTimeout(timeoutID)
    timeoutID = window.setTimeout(function invokeFunc() {
      timeoutID = undefined
      func()
    }, 300)
  }

  window.addEventListener('resize', throttler)

  return () => {
    window.clearTimeout(timeoutID)
    window.removeEventListener('resize', throttler)
  }
}

const MarginProvider: React.FC<React.PropsWithChildren<Props>> = ({children, offset = [0, 0, 0, 0], debug = false}) => {
  const [margin, setMargin] = useState<Margin>(offset)
  const [shouldUpdate, forceUpdate] = useState(0)
  const strategiesRef = useRef<MarginRectStrategy[]>([])
  const strategyEffect = useMemo<MarginStrategyEffect>(() => {
    let timeoutID: number | undefined

    return props => {
      window.clearTimeout(timeoutID)
      timeoutID = window.setTimeout(() => {
        timeoutID = undefined
        forceUpdate(prev => prev + 1)
      }, 600)
      strategiesRef.current.push(props.strategy)

      return () => {
        const index = strategiesRef.current.indexOf(props.strategy)

        if (index > -1) {
          strategiesRef.current.splice(index, 1)
          forceUpdate(prev => prev + 1)
        }
      }
    }
  }, [])

  useEffect(() => {
    function calcMargin() {
      // избегаем первого обновления, ждем пока добавятся все зависимые элементы
      const strategies = shouldUpdate > 0 ? strategiesRef.current : []
      const nextMargin = getNextMargin(strategies, offset)

      setMargin(prevMargin => {
        return isMarginEqual(prevMargin, nextMargin) ? prevMargin : nextMargin
      })
    }

    // вызовем первый раз, чтобы установить первое значение
    calcMargin()
    // потом подпишемся на изменение размера окна, чтобы обновлять при переходе на мобилку
    return resizeEventEffect(calcMargin)
  }, [offset, shouldUpdate])

  if (process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
      if (debug) {
        // не большой хелпер, чтобы визуализировать как работают маргины на карте
        const div = document.createElement('div')
        div.style.cssText = `
          background-color: rgba(255, 0, 0, .3);
          position: absolute;
          z-index: 9000;
          pointer-events: none;
          top: ${margin[0]}px;
          right: ${margin[1]}px;
          bottom: ${margin[2]}px;
          left: ${margin[3]}px;
        `
        document.body.appendChild(div)

        return () => {
          div.remove()
        }
      }
    }, [debug, margin])
  }

  return <Context.Provider value={[margin, strategyEffect]}>{children}</Context.Provider>
}

export default MarginProvider
